import { useCallback } from 'react';
import { siteTemplatesApi } from '../../api/client';
import type { SiteTemplate, UpdateSiteTemplateRequest } from '../../schemas/validation';
import type { AdminTemplateManagerState } from './useAdminTemplateManagerState';

interface UseAdminTemplateManagerHandlersProps {
  state: AdminTemplateManagerState;
}

function isListedTemplate(template: { isPublic?: boolean }): boolean {
  return template.isPublic === true;
}

function placeTemplate(
  templates: SiteTemplate[],
  next: SiteTemplate,
  keep: boolean
): SiteTemplate[] {
  const exists = templates.some(template => template.id === next.id);
  if (!keep) return templates.filter(template => template.id !== next.id);
  if (exists) return templates.map(template => (template.id === next.id ? next : template));
  return [next, ...templates];
}

export const useAdminTemplateManagerHandlers = ({
  state,
}: UseAdminTemplateManagerHandlersProps) => {
  const loadTemplates = useCallback(async () => {
    try {
      state.setIsLoading(true);
      state.setError(null);
      const response = await siteTemplatesApi.browseTemplates({ includePublic: true });
      state.setTemplates(response.templates.filter(isListedTemplate));
    } catch (error) {
      console.error('Failed to load templates:', error);
      state.setError('Failed to load templates');
    } finally {
      state.setIsLoading(false);
    }
  }, [state]);

  const handleSearch = useCallback(
    async (search: string) => {
      try {
        state.setIsLoading(true);
        state.setError(null);
        const response = await siteTemplatesApi.browseTemplates({
          search: search || undefined,
          includePublic: true,
        });
        state.setTemplates(response.templates.filter(isListedTemplate));
      } catch (error) {
        console.error('Failed to search templates:', error);
        state.setError('Failed to search templates');
      } finally {
        state.setIsLoading(false);
      }
    },
    [state]
  );

  const loadPrivateTemplates = useCallback(async () => {
    try {
      state.setIsLoadingPrivate(true);
      state.setError(null);
      const response = await siteTemplatesApi.browsePrivateTemplates();
      state.setPrivateTemplates(response.templates);
    } catch (error) {
      console.error('Failed to load private templates:', error);
      state.setError('Failed to load private templates');
      state.setShowPrivate(false);
    } finally {
      state.setIsLoadingPrivate(false);
    }
  }, [state]);

  const selectTab = useCallback(
    async (tab: 'public' | 'private') => {
      state.setCurrentPage(1);
      if (tab === 'public') {
        state.setShowPrivate(false);
        return;
      }
      state.setShowPrivate(true);
      if (state.privateTemplates !== null) return;
      await loadPrivateTemplates();
    },
    [state, loadPrivateTemplates]
  );

  const handleUpdateTemplate = useCallback(
    async (templateId: string, data: UpdateSiteTemplateRequest) => {
      try {
        state.setIsSubmitting(true);
        state.setError(null);
        const result = await siteTemplatesApi.updateTemplate(templateId, data);
        const current =
          state.templates.find(template => template.id === templateId) ??
          state.privateTemplates?.find(template => template.id === templateId);
        if (current) {
          const next: SiteTemplate = {
            ...current,
            name: result.template.name,
            description: result.template.description,
            tags: result.template.tags,
            isPublic: result.template.isPublic,
            isSystem: result.template.isSystem === true,
            updatedAt: result.template.updatedAt,
          };
          const staysListed = isListedTemplate(next);
          state.setTemplates(prev => placeTemplate(prev, next, staysListed));
          state.setPrivateTemplates(prev =>
            prev === null ? null : placeTemplate(prev, next, !staysListed)
          );
        }
        state.setShowEditModal(false);
        state.setSelectedTemplate(null);
        state.setSuccessMessage('Template updated successfully');
        setTimeout(() => state.setSuccessMessage(null), 3000);
      } catch (error: any) {
        console.error('Failed to update template:', error);
        state.setError(
          error.response?.data?.message || error.message || 'Failed to update template'
        );
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state]
  );

  const handleDeleteTemplate = useCallback(
    async (templateId: string) => {
      try {
        state.setIsSubmitting(true);
        state.setError(null);
        await siteTemplatesApi.deleteTemplate(templateId);
        state.setTemplates(prev => prev.filter(t => t.id !== templateId));
        state.setPrivateTemplates(prev =>
          prev ? prev.filter(template => template.id !== templateId) : prev
        );
        state.setShowDeleteModal(false);
        state.setSelectedTemplate(null);
        state.setSuccessMessage('Template deleted successfully');
        setTimeout(() => state.setSuccessMessage(null), 3000);
      } catch (error: any) {
        console.error('Failed to delete template:', error);
        state.setError(
          error.response?.data?.message || error.message || 'Failed to delete template'
        );
      } finally {
        state.setIsSubmitting(false);
      }
    },
    [state]
  );

  const startEdit = useCallback(
    (template: (typeof state.templates)[0]) => {
      state.setSelectedTemplate(template);
      state.setShowEditModal(true);
    },
    [state]
  );

  const startDelete = useCallback(
    (template: (typeof state.templates)[0]) => {
      state.setSelectedTemplate(template);
      state.setShowDeleteModal(true);
    },
    [state]
  );

  const startPreview = useCallback(
    (template: (typeof state.templates)[0]) => {
      state.setSelectedTemplate(template);
      state.setShowPreviewPanel(true);
    },
    [state]
  );

  return {
    loadTemplates,
    handleSearch,
    selectTab,
    handleUpdateTemplate,
    handleDeleteTemplate,
    startEdit,
    startDelete,
    startPreview,
  };
};
